import { useState, useEffect } from "react";
import {
  Button,
  Box,
  Container,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IProgress,
  ISessionProps,
  SessionStatus,
} from "../../main/components/IProgress";
import { DJCodeBlock, ICodeBlock } from "../../main/components/codeblock";
import { Typography, TextField } from "@appsflyer/fe-ui-core";
import transition from "../../main/components/transition";
import ExplanationTooltip, {
  TooltipType,
} from "../../main/components/ExplanationTooltip";
import {
  amplitudeTrack_continue,
  amplitudeTrack_inputChange,
  InputType,
  sendGtagOnce,
} from "../../services/utils";

const StartSDKiOS = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [devKey, setDevKey] = useState();
  const [appId, setAppId] = useState<string>("");
  const [gcdRequired, setGCDRequired] = useState(false);
  const [timeoutInterval, setTimeoutInterval] = useState("60");
  const [timeoutIntervalDisplay, setTimeoutIntervalDisplay] = useState("60");
  useEffect(() => {
    if (location.state) {
      const state: ISessionProps = location.state as ISessionProps;
      setAppId(state?.appID ?? "");

      const lsDevKey = localStorage.getItem("devKey");
      if (lsDevKey) {
        setDevKey(JSON.parse(lsDevKey));
      }

      if (
        location.state?.progress.find((prog) => prog.id === "startsdk_setgcd")
      ) {
        setGCDRequired(
          location.state?.progress.find((prog) => prog.id === "startsdk_setgcd")
            .isListener
        );
      }
    }
  }, [location.state]);

  const handleClick = () => {
    const progressArr: IProgress[] = location.state?.progress.slice();
    progressArr[progressArr.length - 1].isListener = gcdRequired;

    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      sessionStatus: SessionStatus.ImplementedNotTested,
      currentPage: location.state?.currentPage + 1,
    };

    // send gtag for implemented not test
    sendGtagOnce(
      "implemented_not_tested",
      "StartSDKiOS",
      location.state.sessionId
    );

    amplitudeTrack_continue(location.state, {
      responseListenerRequired: isResponseListener,
    });

    navigate(`/startsdk/${location.state?.os}/testsdktype`, {
      state: progressState,
    });
  };

  const [isDebug, setIsDebug] = useState(true);
  const [isResponseListener, setIsResponseListener] = useState(false);
  const [isDelegate, setIsDelegate] = useState(false);
  const [isATT, setIsATT] = useState(false);
  const [isCUID, setIsCUID] = useState(false);
  const [codePrimary, setCodePrimary] = useState("");
  const [codeSecondary, setCodeSecondary] = useState("");

  useEffect(() => {
    setCodePrimary(`import UIKit
import AppsFlyerLib
${isATT ? `import AppTrackingTransparency` : ``}
      
@UIApplicationMain
class AppDelegate: UIResponder, UIApplicationDelegate {     
          
    func application(_ application: UIApplication, didFinishLaunchingWithOptions launchOptions: 
    [UIApplication.LaunchOptionsKey: Any]?) -> Bool {
        // set the App ID & the DevKey
        AppsFlyerLib.shared().appleAppID = "${appId}"
        AppsFlyerLib.shared().appsFlyerDevKey = "${devKey}"
        ${
          isDebug
            ? `
        // Debug logs
        AppsFlyerLib.shared().isDebug = true
`
            : ``
        }${
      isATT
        ? `
        // Only for ATT
        AppsFlyerLib.shared().waitForATTUserAuthorization(timeoutInterval: ${timeoutInterval})
`
        : ""
    }${
      isDelegate
        ? `
        // For Scene Delegate
        NotificationCenter.default.addObserver(self, selector: #selector(didBecomeActiveNotification),
        name: UIApplication.didBecomeActiveNotification, 
        object: nil)
`
        : ""
    }${
      isCUID
      ? `
        // Set CUID
        AppsFlyerLib.shared().customerUserID = "my user id"
`
      : ""
    }    ${isResponseListener
            ? `
        // With Listener
        AppsFlyerLib.shared().start(completionHandler: { (dictionary, error) in
          if (error != nil){
              print(error ?? "")
              return
          } else {
              print(dictionary ?? "")
              return
          }
        })` : `
        // No Listener
        AppsFlyerLib.shared().start()`
        }
    }
    ${
      isDelegate && isATT ? ` 
      // For Scene Delegate & App Delegate
      @objc func didBecomeActiveNotification() {
        // Only for ATT
        if #available(iOS 14, *) {
              ATTrackingManager.requestTrackingAuthorization { (status) in
                switch status {
                case .denied:
                    print("AuthorizationStatus is denied")
                case .notDetermined:
                    print("AuthorizationStatus is notDetermined")
                case .restricted:
                    print("AuthorizationStatus is restricted")
                case .authorized:
                    print("AuthorizationStatus is authorized")
                @unknown default:
                    fatalError("Invalid authorization status")
                }
              }
        }
      }
      
      ` : isDelegate ? `
      // For Scene Delegate
      @objc func didBecomeActiveNotification() {
      }` : isATT ? `
      // For App Delegate
      func applicationDidBecomeActive(_ application: UIApplication) {
        // Only for ATT
          if #available(iOS 14, *) {
                ATTrackingManager.requestTrackingAuthorization { (status) in
                  switch status {
                  case .denied:
                      print("AuthorizationStatus is denied")
                  case .notDetermined:
                      print("AuthorizationStatus is notDetermined")
                  case .restricted:
                      print("AuthorizationStatus is restricted")
                  case .authorized:
                      print("AuthorizationStatus is authorized")
                  @unknown default:
                      fatalError("Invalid authorization status")
                  }
                }
          }
      }` : `` }
}`);

    setCodeSecondary(`#import <UIKit/UIKit.h>
#import <AppsFlyerLib/AppsFlyerLib.h>
${isATT ? `#import <AppTrackingTransparency/AppTrackingTransparency.h>` : ``}

@interface AppDelegate ()

@end

@implementation AppDelegate

@synthesize conversionData;

- (BOOL)application:(UIApplication *)application didFinishLaunchingWithOptions:(NSDictionary *)launchOptions {
  ${
    isDebug
      ? `
    [AppsFlyerLib shared].isDebug = YES;
`
        : ``
    }${
      isATT
        ? `
    // Only for ATT
    [[AppsFlyerLib shared] waitForATTUserAuthorizationWithTimeoutInterval:${timeoutInterval}];
`
        : ""
    }${
      isDelegate
        ? `
    // For Scene Delegate
    [[NSNotificationCenter defaultCenter] addObserver:self
                                          selector:@selector(didBecomeActiveNotification)
                                          name:UIApplicationDidBecomeActiveNotification
                                          object:nil];
`
        : ""
    }${
      isCUID
      ? `
    // Set CUID
    [AppsFlyerLib shared].customerUserID = @"my user id";
`
      : ""
    }    ${isResponseListener
            ? `
    // With Listener
    [[AppsFlyerLib shared] startWithCompletionHandler:^(NSDictionary<NSString *,id> *dictionary, NSError *error) {
        if (error != nil) {
            NSLog(@"%@", error);
            return;
        } else {
            NSLog(@"%@", dictionary);
            return;
        }
    }];` : `
    // No Listener
    [[AppsFlyerLib shared] start];`
    }
    
    return YES;
}
    ${
      isDelegate && isATT ? ` 
    // For Scene Delegate & App Delegate
    - (void)didBecomeActiveNotification {
        // Only for ATT
        if (@available(iOS 14, *)) {
            [ATTrackingManager requestTrackingAuthorizationWithCompletionHandler:^(ATTrackingManagerAuthorizationStatus status) {
                switch (status) {
                    case ATTrackingManagerAuthorizationStatusDenied:
                        NSLog(@"AuthorizationStatus is denied");
                        break;
                    case ATTrackingManagerAuthorizationStatusNotDetermined:
                        NSLog(@"AuthorizationStatus is notDetermined");
                        break;
                    case ATTrackingManagerAuthorizationStatusRestricted:
                        NSLog(@"AuthorizationStatus is restricted");
                        break;
                    case ATTrackingManagerAuthorizationStatusAuthorized:
                        NSLog(@"AuthorizationStatus is authorized");
                        break;
                    default:
                        NSLog(@"Invalid authorization status");
                        break;
                }
            }];
        }
    }
    
    ` : isDelegate ? `
    // For Scene Delegate
    - (void)didBecomeActiveNotification {
    }` : isATT ? `
    // For App Delegate
    - (void)applicationDidBecomeActive:(UIApplication *)application {
        // Only for ATT
        if (@available(iOS 14, *)) {
            [ATTrackingManager requestTrackingAuthorizationWithCompletionHandler:^(ATTrackingManagerAuthorizationStatus status) {
                switch (status) {
                    case ATTrackingManagerAuthorizationStatusDenied:
                        NSLog(@"AuthorizationStatus is denied");
                        break;
                    case ATTrackingManagerAuthorizationStatusNotDetermined:
                        NSLog(@"AuthorizationStatus is notDetermined");
                        break;
                    case ATTrackingManagerAuthorizationStatusRestricted:
                        NSLog(@"AuthorizationStatus is restricted");
                        break;
                    case ATTrackingManagerAuthorizationStatusAuthorized:
                        NSLog(@"AuthorizationStatus is authorized");
                        break;
                    default:
                        NSLog(@"Invalid authorization status");
                        break;
                }
            }];
        }
    }` : `` }
`);
  }, [
    isDebug,
    isDelegate,
    isATT,
    isCUID,
    isResponseListener,
    timeoutInterval,
    devKey,
    appId,
  ]);

  const setCodeBlock = () => {};

  const codeProps: ICodeBlock = {
    codePrimary: codePrimary,
    codeSecondary: codeSecondary,
    language: "swift",
    showLineNumbers: true,
  };

  const handleResponseListenerChange = () => {
    const newValue = !isResponseListener;
    setIsResponseListener(newValue);
    amplitudeTrack_inputChange(
      location.state,
      InputType.Checkbox,
      "responseListenerRequired",
      newValue
    );
    setCodeBlock();
  };

  const handleIsDebugChange = () => {
    const newValue = !isDebug;
    setIsDebug(newValue);
    amplitudeTrack_inputChange(
      location.state,
      InputType.Checkbox,
      "isDebug",
      newValue
    );
    setCodeBlock();
  };

  const handleIsDelegateChange = () => {
    const newValue = !isDelegate;
    setIsDelegate(newValue);
    amplitudeTrack_inputChange(
      location.state,
      InputType.Checkbox,
      "SceneDelegate",
      newValue
    );
    setCodeBlock();
  };

  const handleIsATTChange = () => {
    const newValue = !isATT;
    setIsATT(newValue);
    amplitudeTrack_inputChange(
      location.state,
      InputType.Checkbox,
      "ATT",
      newValue
    );
    setCodeBlock();
  };

  const handleIsCUIDChange = () => {
    const newValue = !isCUID;
    setIsCUID(newValue);
    amplitudeTrack_inputChange(
      location.state,
      InputType.Checkbox,
      "CUID",
      newValue
    );
    setCodeBlock();
  };

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Typography variant="h1">{t("start_sdk")}</Typography>
        <>
          <Box display={"flex"} gap={5} mt={1} mb={-1} >
            <Box display={"flex"} flexWrap={"wrap"} gap={2}>
              {/* Debug */}
              <Box my={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isDebug}
                      onChange={handleIsDebugChange}
                      disableRipple
                    />
                  }
                  label={
                    <Typography>
                      {t("testConsole_TestType_manualTest_desc_c")}
                    </Typography>
                  }
                />
              </Box>

              {/* Scene Delegate */}
              <Box my={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isDelegate}
                      onChange={handleIsDelegateChange}
                      disableRipple
                    />
                  }
                  label={
                    <Typography>
                      {t("StartSDK_startSDK_ios_step3_SceneDelegate")}
                    </Typography>
                  }
                />
              </Box>

              {/* Listener */}
              <Box my={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isResponseListener}
                      onChange={handleResponseListenerChange}
                      disableRipple
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography>
                        {t("responseListenerExp_title")}
                        <ExplanationTooltip />
                      </Typography>
                      
                    </Box>
                  }
                />
              </Box>

              {/* ATT */}
              <Box my={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isATT}
                      onChange={handleIsATTChange}
                      disableRipple
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography>
                        {t("StartSDK_startSDK_ios_step3_ATT")}
                        <ExplanationTooltip tooltipType={TooltipType.ATT} />

                      </Typography>
                    </Box>
                  }
                />
                {isATT && (
                  <Box mt={1.5}>
                    <TextField
                      label={
                        <Typography>
                          {t("StartSDK_startSDK_ios_step3_ATT_interval")}
                          <ExplanationTooltip
                            tooltipType={TooltipType.ATT_Interval}
                          />
                        </Typography>
                      }
                      value={timeoutIntervalDisplay}
                      inputProps={{ min: 0, max: 600 }}
                      type={"number"}
                      size={"s"}
                      error={Number(timeoutIntervalDisplay) > 600}
                      helperText={Number(timeoutIntervalDisplay) > 600 ? (
                        <Typography>
                          {t("StartSDK_startSDK_ios_step3_ATT_interval_error")}
                        </Typography>
                      ) : ""}
                      onKeyDown={(e) => {
                        // Allow: backspace, delete, tab, escape, enter, and arrow keys
                        if (
                          ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key) ||
                          // Allow Ctrl/Cmd + A, C, V, X
                          (e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())
                        ) {
                          return;
                        }
                        if (!/^[0-9]$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const inputValue = e.currentTarget.value;
                        const numericValue = inputValue.replace(/[^0-9]/g, "");
                        if (numericValue === "") {
                          setTimeoutIntervalDisplay("");
                          setTimeoutInterval("0");
                          return;
                        }
                        const numValue = Number(numericValue);
                        setTimeoutIntervalDisplay(numericValue);
                        if (numValue > 600) {
                          setTimeoutInterval("600");
                        } else {
                          const clampedValue = Math.max(0, numValue);
                          setTimeoutInterval(clampedValue.toString());
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData("text");
                        const numericValue = pastedText.replace(/[^0-9]/g, "");
                        if (numericValue !== "") {
                          const numValue = Number(numericValue);
                          setTimeoutIntervalDisplay(numericValue);
                          if (numValue > 600) {
                            setTimeoutInterval("600");
                          } else {
                            const clampedValue = Math.max(0, numValue);
                            setTimeoutInterval(clampedValue.toString());
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* CUID */}
              <Box my={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCUID}
                      onChange={handleIsCUIDChange}
                      disableRipple
                    />
                  }
                  label={
                    <Typography>
                      {t("StartSDK_startSDK_ios_step3_CUID")}
                    </Typography>
                  }
                />

              </Box>

            </Box>
          </Box>
          <DJCodeBlock {...codeProps} />
        </>
      </Box>
      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        {" "}
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={() => handleClick()}
        >
          {t("general_Continue")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(StartSDKiOS);
